import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import {
  onDemandGLARecordedStatusSchema,
  sessionFeedbackIssueSchema,
  sessionFeedbackLevelSchema,
} from './session-feedbacks.js';
import { studentFeedbackIssueSchema, studentFeedbackStatusSchema } from './student-feedback-shared.js';
import {
  glaFollowUpStatusSchema,
  studentContactTypeSchema,
  studentStatusSchema,
  studentTagSchema,
  studentYearNullableSchema,
} from './student.js';
import { userIdentityStatusSchema } from './user-identity.js';
import { roleSchema, userRoleStatusSchema } from './user-role.js';

export const purgeDataDryRunReportSchema = z.object({
  id: z.string(),
  districtName: z.string(),
  schoolId: z.string(),
  schoolName: z.string(),
  studentId: z.string(),
  grade: studentYearNullableSchema,
  startedAtInstant: z.instanceof(Temporal.Instant),
  endedAtInstant: z.instanceof(Temporal.Instant),
  report: z.string(),
  reportFromAI: z.string().nullable(),
  reportFromAISpanish: z.string().nullable(),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
  sessions: z.array(
    z.object({
      id: z.string(),
      startInstant: z.instanceof(Temporal.Instant),
    }),
  ),
});
export type PurgeDataDryRunReport = z.infer<typeof purgeDataDryRunReportSchema>;

const dryRunSessionColumns = {
  sessionId: z.string(),
  sessionStartInstant: z.instanceof(Temporal.Instant),
  sessionDistrictName: z.string(),
  sessionSchoolId: z.string(),
  sessionSchoolName: z.string(),
};

export const purgeDataDryRunSchoolReportSessionSchema = z.object({
  schoolReportId: z.string(),
  studentId: z.string(),
  reportDistrictName: z.string(),
  reportSchoolId: z.string(),
  reportSchoolName: z.string(),
  ...dryRunSessionColumns,
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunSchoolReportSession = z.infer<typeof purgeDataDryRunSchoolReportSessionSchema>;

export const purgeDataDryRunAssessmentResultSchema = z.object({
  id: z.string(),
  ...dryRunSessionColumns,
  coachId: z.string(),
  studentId: z.string(),
  assessmentId: z.string(),
  assessmentName: z.string(),
  assessmentContentId: z.string(),
  assessmentContentName: z.string(),
  assessmentFinished: z.boolean().nullable(),
  incorrectWords: z.array(z.string()),
  totalWordsRead: z.number().nullable(),
  incorrectWordsCount: z.number().nullable(),
  phonemicReadingResult: z.boolean().nullable(),
  comprehensionSkillResult: z.string().nullable(),
  fluencySkillResult: z.string().nullable(),
  comprehensionPassageResult: z.boolean().nullable(),
  canRetell: z.boolean().nullable(),
  incorrectlWordsReadFromPMList: z.number().nullable(),
  finishedEarly: z.boolean().nullable(),
  totalSecondsSpent: z.number().nullable(),
  lastUpdatedById: z.string().nullable(),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunAssessmentResult = z.infer<typeof purgeDataDryRunAssessmentResultSchema>;

export const purgeDataDryRunSessionFeedbackSchema = z.object({
  ...dryRunSessionColumns,
  coachId: z.string(),
  studentId: z.string(),
  feedbackContent: z.string().nullable(),
  majorConcerns: z.string().nullable(),
  otherTechIssues: z.string().nullable(),
  issues: z.array(sessionFeedbackIssueSchema),
  levels: z.array(sessionFeedbackLevelSchema),
  studentAttended: z.boolean().nullable(),
  feedbackSubmitted: z.boolean(),
  lateFeedbackDismissed: z.boolean().nullable(),
  submittedHoursFromSessionEnd: z.number().nullable(),
  dailySummary: z.string().nullable(),
  submittedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  draftSavedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunSessionFeedback = z.infer<typeof purgeDataDryRunSessionFeedbackSchema>;

export const purgeDataDryRunSessionFeedbackTimeTrackingSchema = z.object({
  id: z.string(),
  ...dryRunSessionColumns,
  coachId: z.string(),
  studentId: z.string(),
  actorId: z.string(),
  firstStartedAtInstant: z.instanceof(Temporal.Instant),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunSessionFeedbackTimeTracking = z.infer<
  typeof purgeDataDryRunSessionFeedbackTimeTrackingSchema
>;

const dryRunStudentSchoolColumns = {
  studentSchools: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      districtName: z.string(),
    }),
  ),
};

export const purgeDataDryRunStudentFeedbackSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  ...dryRunStudentSchoolColumns,
  studentFeedbackIssue: studentFeedbackIssueSchema,
  status: studentFeedbackStatusSchema,
  relevelNeeded: z.boolean(),
  learningPathId: z.string().nullable(),
  learningPathName: z.string().nullable(),
  startingLessonId: z.string().nullable(),
  startingLessonName: z.string().nullable(),
  reviewerId: z.string(),
  note: z.string(),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunStudentFeedback = z.infer<typeof purgeDataDryRunStudentFeedbackSchema>;

export const purgeDataDryRunOnDemandGLASchema = z.object({
  id: z.string(),
  ...dryRunSessionColumns,
  studentId: z.string(),
  sourceSessionId: z.string().nullable(),
  sourceCoachId: z.string().nullable(),
  lessonId: z.string(),
  lessonName: z.string(),
  recordedStatus: onDemandGLARecordedStatusSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunOnDemandGLA = z.infer<typeof purgeDataDryRunOnDemandGLASchema>;

export const purgeDataDryRunStudentSessionSchema = z.object({
  ...dryRunSessionColumns,
  studentId: z.string(),
  joinedAtInstants: z.array(z.instanceof(Temporal.Instant)),
  leftAtInstants: z.array(z.instanceof(Temporal.Instant)),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunStudentSession = z.infer<typeof purgeDataDryRunStudentSessionSchema>;

export const purgeDataDryRunStudentContactSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  ...dryRunStudentSchoolColumns,
  type: studentContactTypeSchema,
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunStudentContact = z.infer<typeof purgeDataDryRunStudentContactSchema>;

export const purgeDataDryRunStudentShortLinkSchema = z.object({
  shortCode: z.string(),
  userId: z.string(),
  ...dryRunStudentSchoolColumns,
  isActive: z.boolean(),
  isPaused: z.boolean(),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunStudentShortLink = z.infer<typeof purgeDataDryRunStudentShortLinkSchema>;

export const purgeDataDryRunStudentStatusAuditSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  ...dryRunStudentSchoolColumns,
  fromStatus: studentStatusSchema,
  toStatus: studentStatusSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunStudentStatusAudit = z.infer<typeof purgeDataDryRunStudentStatusAuditSchema>;

export const purgeDataDryRunUserSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  timeZone: z.string().nullable(),
  iss: z.string().nullable(),
  sub: z.string().nullable(),
  isRegistrable: z.boolean(),
  ...dryRunStudentSchoolColumns,
  roles: z.array(
    z.object({
      role: roleSchema,
      status: userRoleStatusSchema,
      schoolId: z.string().nullable(),
      schoolName: z.string().nullable(),
      districtName: z.string().nullable(),
    }),
  ),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunUser = z.infer<typeof purgeDataDryRunUserSchema>;

export const purgeDataDryRunUserRoleSchema = z.object({
  id: z.string(),
  userId: z.string(),
  role: roleSchema,
  status: userRoleStatusSchema,
  /** Null for roles that are not tied to a school, such as `air_admin`. */
  schoolId: z.string().nullable(),
  schoolName: z.string().nullable(),
  districtName: z.string().nullable(),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunUserRole = z.infer<typeof purgeDataDryRunUserRoleSchema>;

export const purgeDataDryRunUserIdentitySchema = z.object({
  id: z.string(),
  userId: z.string(),
  ...dryRunStudentSchoolColumns,
  iss: z.string(),
  sub: z.string().nullable(),
  status: userIdentityStatusSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunUserIdentity = z.infer<typeof purgeDataDryRunUserIdentitySchema>;

export const purgeDataDryRunStudentSchema = z.object({
  id: z.string(),
  ...dryRunStudentSchoolColumns,
  grade: studentYearNullableSchema,
  status: studentStatusSchema.nullable(),
  fleid: z.string().nullable(),
  specialEd: z.boolean(),
  dyslexic: z.boolean(),
  ell: z.boolean(),
  classroomTeacher: z.string().nullable(),
  studentTags: z.array(studentTagSchema),
  learningPathId: z.string().nullable(),
  learningPathName: z.string().nullable(),
  startingLessonId: z.string().nullable(),
  startingLessonName: z.string().nullable(),
  nextSessionLessonId: z.string().nullable(),
  nextSessionLessonName: z.string().nullable(),
  requestedClassTimeId: z.string().nullable(),
  phonicsSkill1Mastered: z.number().nullable(),
  phonicsSkill2Mastered: z.number().nullable(),
  phonicsSkill3Mastered: z.number().nullable(),
  phonicsSkill4Mastered: z.number().nullable(),
  morphemeSkill1Mastered: z.number().nullable(),
  morphemeSkill2Mastered: z.number().nullable(),
  morphemeSkill3Mastered: z.number().nullable(),
  averageAttendanceLast14Days: z.number().nullable(),
  averageAttendanceLast14DaysCalculatedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  totalFutureSessions: z.number().nullable(),
  nextSessionPlainDate: z.string().nullable(),
  glaFollowUpStatus: glaFollowUpStatusSchema.nullable(),
  glaReviewerId: z.string().nullable(),
  glaReviewNotes: z.string().nullable(),
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
});
export type PurgeDataDryRunStudent = z.infer<typeof purgeDataDryRunStudentSchema>;

const dryRunUserAssociationColumns = {
  internalId: z.string(),
  externalId: z.string(),
  ...dryRunStudentSchoolColumns,
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
};

export const purgeDataDryRunEdlinkUserAssociationSchema = z.object(dryRunUserAssociationColumns);
export type PurgeDataDryRunEdlinkUserAssociation = z.infer<typeof purgeDataDryRunEdlinkUserAssociationSchema>;

export const purgeDataDryRunV1UserAssociationSchema = z.object(dryRunUserAssociationColumns);
export type PurgeDataDryRunV1UserAssociation = z.infer<typeof purgeDataDryRunV1UserAssociationSchema>;

export const purgeDryRunTableSchema = z.enum([
  'schoolReportSessions',
  'schoolReports',
  'assessmentResults',
  'onDemandGLAs',
  'sessionFeedbacks',
  'sessionFeedbackTimeTrackings',
  'studentFeedbacks',
  'studentSessions',
  'studentContacts',
  'studentShortLinks',
  'studentStatusAudits',
  'edlinkUserAssociations',
  'v1UserAssociations',
  'userIdentities',
  'userRoles',
  'students',
  'users',
]);
export type PurgeDryRunTable = z.infer<typeof purgeDryRunTableSchema>;

/**
 * Order for the dry run and the purge to avoid automatic cascades on delete
 */
export const PurgeDryRunTables = purgeDryRunTableSchema.options;

/**
 * Type check to keep Prisma enum and purgeDryRunTableSchema in sync
 */
type Assert<T extends true> = T;
type Equals<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
export type PurgeTableNamesMatchPrismaEnum = Assert<Equals<PurgeDryRunTable, PrismaEnums.PurgeRequestTableName>>;

export const purgeDryRunPartQuerySchema = z.object({
  part: z.coerce.number().int().positive().default(1),
});
export type PurgeDryRunPartQuery = z.infer<typeof purgeDryRunPartQuerySchema>;

const purgeDryRunResultBase = {
  scopeLabel: z.string(),
  /** Which part of the table this export covers, counting from 1. */
  part: z.number().int().positive(),
  /** Rows matching the purge scope across every part. */
  totalRows: z.number().int().nonnegative(),
};

export const purgeDryRunResultSchema = z.discriminatedUnion('table', [
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('schoolReports'),
    rows: z.array(purgeDataDryRunReportSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('schoolReportSessions'),
    rows: z.array(purgeDataDryRunSchoolReportSessionSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('assessmentResults'),
    rows: z.array(purgeDataDryRunAssessmentResultSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('sessionFeedbacks'),
    rows: z.array(purgeDataDryRunSessionFeedbackSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('sessionFeedbackTimeTrackings'),
    rows: z.array(purgeDataDryRunSessionFeedbackTimeTrackingSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('studentFeedbacks'),
    rows: z.array(purgeDataDryRunStudentFeedbackSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('onDemandGLAs'),
    rows: z.array(purgeDataDryRunOnDemandGLASchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('studentSessions'),
    rows: z.array(purgeDataDryRunStudentSessionSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('studentContacts'),
    rows: z.array(purgeDataDryRunStudentContactSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('studentShortLinks'),
    rows: z.array(purgeDataDryRunStudentShortLinkSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('studentStatusAudits'),
    rows: z.array(purgeDataDryRunStudentStatusAuditSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('edlinkUserAssociations'),
    rows: z.array(purgeDataDryRunEdlinkUserAssociationSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('v1UserAssociations'),
    rows: z.array(purgeDataDryRunV1UserAssociationSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('userIdentities'),
    rows: z.array(purgeDataDryRunUserIdentitySchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('userRoles'),
    rows: z.array(purgeDataDryRunUserRoleSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('students'),
    rows: z.array(purgeDataDryRunStudentSchema),
  }),
  z.object({
    ...purgeDryRunResultBase,
    table: z.literal('users'),
    rows: z.array(purgeDataDryRunUserSchema),
  }),
]);
export type PurgeDryRunResult = z.infer<typeof purgeDryRunResultSchema>;

export const purgeDistrictDataOptionsSchema = z.object({
  districtName: z.string().trim().min(1, 'Type the district name to confirm.'),
});
export type PurgeDistrictDataOptions = z.infer<typeof purgeDistrictDataOptionsSchema>;

export const purgeEnrolledStudentSchema = z.object({
  studentId: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  grade: studentYearNullableSchema,
  ...dryRunStudentSchoolColumns,
});
export type PurgeEnrolledStudent = z.infer<typeof purgeEnrolledStudentSchema>;

export const PURGE_ENROLLED_STUDENT_SAMPLE_SIZE = 20;

export const purgeEnrolledStudentsSchema = z.object({
  scopeLabel: z.string(),
  enrolledCount: z.number().int().nonnegative(),
  students: z.array(purgeEnrolledStudentSchema),
});
export type PurgeEnrolledStudents = z.infer<typeof purgeEnrolledStudentsSchema>;

export const { PurgeActionStatus } = PrismaEnums;
export type PurgeActionStatus = PrismaEnums.PurgeActionStatus;
export const purgeActionStatusSchema = z.nativeEnum(PurgeActionStatus);

export const purgeSchoolDataRequestOptionsSchema = z.object({
  schoolName: z.string().trim().min(1, 'Type the school name to confirm.'),
});
export type PurgeSchoolDataRequestOptions = z.infer<typeof purgeSchoolDataRequestOptionsSchema>;

export const PURGE_CONFIRMATION_SUFFIX = 'confirm permanent delete';
export const purgeConfirmationPhrase = (name: string) => `${name.trim()} ${PURGE_CONFIRMATION_SUFFIX}`;

export const purgeTableResultSchema = z.object({
  table: purgeDryRunTableSchema,
  status: purgeActionStatusSchema,
  rowsExpected: z.number().int().nonnegative(),
  rowsDeleted: z.number().int().nonnegative().nullable(),
  errorMessage: z.string().nullable(),
});
export type PurgeTableResult = z.infer<typeof purgeTableResultSchema>;

export const purgeDataRequestResultSchema = z.object({
  purgeRequestId: z.string(),
  status: purgeActionStatusSchema,
  message: z.string(),
  tables: z.array(purgeTableResultSchema),
});
export type PurgeDataRequestResult = z.infer<typeof purgeDataRequestResultSchema>;
